import { useState } from 'react';
import {
	CalendarPlus,
	ChevronDown,
	ChevronUp,
	ClipboardList,
	Mail,
	Pencil,
	Phone,
	Trash2,
	UserRound,
} from 'lucide-react';
import {
	buildPatientDetailSections,
	DetailSectionBlock,
	formatCpfDisplay,
	formatPhoneDisplay,
} from '@/utils/participantDetailDisplay';
import { PATIENT_GENDER_LABELS } from '@/constants/patient';
import { PatientWhatsAppStatusIcon } from '@/components/whatsapp/PatientWhatsAppStatusIcon';
import { isPatientWhatsAppNotificationsActive } from '@/utils/patientWhatsAppNotifications';

type ProfessionalPatientRowProps = {
	patient: Patients.Patient;
	onEdit: (patient: Patients.Patient) => void;
	onDelete: (patient: Patients.Patient) => void;
	onSchedule?: (patient: Patients.Patient) => void;
	onOpenProntuario?: (patient: Patients.Patient) => void;
};

const ProfessionalPatientRow = ({
	patient,
	onEdit,
	onDelete,
	onSchedule,
	onOpenProntuario,
}: ProfessionalPatientRowProps) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const cpfLabel = formatCpfDisplay(patient.cpf);
	const phoneLabel = formatPhoneDisplay(patient.phone);
	const genderLabel = patient.gender
		? PATIENT_GENDER_LABELS[patient.gender]
		: null;
	const whatsAppActive = isPatientWhatsAppNotificationsActive(patient);
	const initials = patient.full_name
		.split(' ')
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part.charAt(0).toUpperCase())
		.join('');

	const toggleExpanded = (event: React.MouseEvent) => {
		event.stopPropagation();
		setIsExpanded((prev) => !prev);
	};

	const expandButton = (className?: string) => (
		<button
			type="button"
			onClick={toggleExpanded}
			className={
				className ??
				'flex items-center justify-center rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900'
			}
			title={isExpanded ? 'Recolher informações' : 'Ver informações'}
		>
			{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
		</button>
	);

	return (
		<div className="group relative rounded-2xl border border-stone-100 bg-white p-4 shadow-sm transition-all duration-200 hover:border-stone-200">
			{expandButton('absolute right-3 top-3 flex sm:hidden')}
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
				<div className="flex min-w-0 items-center gap-4 pr-8 sm:pr-0">
					<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-stone-100 text-sm font-black uppercase text-stone-600">
						{initials || '?'}
					</div>
					<div className="min-w-0">
						<div className="flex min-w-0 items-center gap-1.5">
							<p className="truncate text-sm font-bold text-stone-900">
								{patient.full_name}
							</p>
							<PatientWhatsAppStatusIcon
								enabled={whatsAppActive}
							/>
						</div>
						<p className="mt-1 text-[10px] font-bold uppercase tracking-tight text-stone-400">
							{cpfLabel ? `CPF ${cpfLabel}` : 'CPF não informado'}
						</p>
						{(genderLabel || phoneLabel) && (
							<p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] font-bold text-stone-500">
								{genderLabel && (
									<span className="whitespace-nowrap">
										{genderLabel}
									</span>
								)}
								{genderLabel && phoneLabel && (
									<span className="text-stone-300">·</span>
								)}
								{phoneLabel && (
									<span className="whitespace-nowrap">
										{phoneLabel}
									</span>
								)}
							</p>
						)}
					</div>
				</div>

				<div className="flex w-full items-center justify-end gap-2 border-t border-stone-100 pt-3 sm:w-auto sm:border-t-0 sm:pt-0">
					<div className="flex items-center gap-1">
						{onOpenProntuario && (
							<button
								type="button"
								onClick={(event) => {
									event.stopPropagation();
									onOpenProntuario(patient);
								}}
								className="flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 sm:rounded-lg sm:border-0 sm:px-2 sm:py-2 sm:text-stone-400"
								title="Ver prontuário do paciente"
							>
								<span className="sm:hidden">Prontuário </span>
								<ClipboardList
									size={16}
									className="sm:size-4"
								/>
							</button>
						)}
						{onSchedule && (
							<button
								type="button"
								onClick={(event) => {
									event.stopPropagation();
									onSchedule(patient);
								}}
								className="flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700 sm:rounded-lg sm:border-0 sm:px-2 sm:py-2 sm:text-stone-400"
								title="Agendar consulta"
							>
								<span className="sm:hidden">Agendar </span>
								<CalendarPlus size={16} className="sm:size-4" />
							</button>
						)}
						<button
							type="button"
							onClick={(event) => {
								event.stopPropagation();
								onEdit(patient);
							}}
							className="flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 transition-colors hover:bg-stone-50 hover:text-stone-900 sm:rounded-lg sm:border-0 sm:px-2 sm:py-2 sm:text-stone-400"
							title="Editar paciente"
						>
							<span className="sm:hidden">Editar </span>
							<Pencil size={16} className="sm:size-4" />
						</button>
						<button
							type="button"
							onClick={(event) => {
								event.stopPropagation();
								onDelete(patient);
							}}
							className="flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 transition-colors hover:bg-rose-50 hover:text-rose-600 sm:rounded-lg sm:border-0 sm:px-2 sm:py-2 sm:text-stone-400"
							title="Excluir paciente"
						>
							<span className="sm:hidden">Excluir </span>
							<Trash2 size={16} className="sm:size-4" />
						</button>
						{expandButton('hidden sm:flex')}
					</div>
				</div>
			</div>

			{isExpanded && (
				<div className="mt-4 space-y-4 border-t border-stone-100 pt-4">
					<div className="grid grid-cols-1 gap-4 text-sm text-stone-900 sm:grid-cols-2">
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								Nome
							</p>
							<p className="mt-1 flex items-center gap-2 font-semibold text-stone-900">
								<UserRound className="h-4 w-4 shrink-0 text-stone-500" />
								<span>{patient.full_name}</span>
							</p>
						</div>
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								E-mail
							</p>
							<p className="mt-1 flex items-center gap-2 font-semibold text-stone-900">
								<Mail className="h-4 w-4 shrink-0 text-stone-500" />
								<span>{patient.email?.trim() || '—'}</span>
							</p>
						</div>
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								Telefone
							</p>
							<p className="mt-1 flex items-center gap-2 font-semibold text-stone-900">
								<Phone className="h-4 w-4 shrink-0 text-stone-500" />
								<span>{phoneLabel || '—'}</span>
							</p>
						</div>
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								CPF
							</p>
							<p className="mt-1 font-semibold text-stone-900">
								{cpfLabel || '—'}
							</p>
						</div>
					</div>

					<div className="flex flex-col gap-3">
						{buildPatientDetailSections(patient, 'scheduling').map(
							(section) => (
								<DetailSectionBlock
									key={section.title}
									{...section}
								/>
							),
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export { ProfessionalPatientRow };
